import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Users,
  UserPlus,
  Edit,
  Trash2,
  FolderOpen,
  Shield,
  Key,
  Grid3x3,
  List,
  LogIn,
  Search,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  UserX
} from 'lucide-react';
import userService from '../services/userService';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { DEPARTMENTS, POSITIONS } from '../constants/company';

const UsersPage = () => {
  const { user: currentUser, refreshUser, impersonate } = useAuth();
  
  // Odśwież dane użytkownika tylko raz przy załadowaniu strony
  useEffect(() => {
    if (refreshUser) {
      refreshUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Pusta tablica - wykonaj tylko raz przy montowaniu
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showBulkFolderModal, setShowBulkFolderModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [viewMode, setViewMode] = useState('list'); // 'grid' lub 'list'
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'active' | 'inactive'
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [openMenuUserId, setOpenMenuUserId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const actionsMenuRef = useRef(null);
  const actionsMenuTriggerRef = useRef(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!openMenuUserId) return undefined;
    const closeMenu = () => {
      setOpenMenuUserId(null);
      setMenuAnchor(null);
    };
    const handleClickOutside = (event) => {
      const insideMenu = actionsMenuRef.current && actionsMenuRef.current.contains(event.target);
      const insideTrigger = actionsMenuTriggerRef.current && actionsMenuTriggerRef.current.contains(event.target);
      if (!insideMenu && !insideTrigger) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [openMenuUserId]);

  const openActionsMenu = (event, userId) => {
    if (openMenuUserId === userId) {
      setOpenMenuUserId(null);
      setMenuAnchor(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 224;
    const menuHeight = 120;
    const openUpward = rect.bottom + menuHeight > window.innerHeight - 8;
    setMenuAnchor({
      top: openUpward ? rect.top - menuHeight - 4 : rect.bottom + 4,
      left: Math.min(Math.max(rect.right - menuWidth, 8), window.innerWidth - menuWidth - 8)
    });
    setOpenMenuUserId(userId);
  };

  useEffect(() => {
    setCurrentPage(1);
    setSelectedUserIds([]);
  }, [activeTab, searchQuery]);

  const { data: users, isLoading: usersLoading } = useQuery(
    'users',
    userService.getUsers,
    {
      staleTime: 0, // Dane są zawsze przestarzałe, pobierz świeże
      cacheTime: 0, // Nie cache'uj danych
      refetchOnMount: true, // Zawsze pobierz przy montowaniu
      refetchOnWindowFocus: true // Pobierz przy powrocie do okna
    }
  );

  const { data: devices } = useQuery(
    'nas-devices',
    () => import('../services/nasService').then(service => service.nasService.getNASDevices())
  );

  const { data: availableFolders, isLoading: foldersLoading } = useQuery(
    'available-folders',
    userService.getAvailableFolders
  );

  const { data: folderAccessSummaryData } = useQuery(
    'folder-access-summary',
    userService.getFolderAccessSummary
  );
  const folderAccessSummary = folderAccessSummaryData?.summary || {};

  const { data: userFolderAccess, isLoading: userAccessLoading } = useQuery(
    ['user-folder-access', selectedUser?._id],
    () => userService.getUserFolderAccess(selectedUser?._id),
    {
      enabled: !!selectedUser?._id
    }
  );

  const createUserMutation = useMutation(
    userService.createUser,
    {
      onSuccess: () => {
        toast.success('Użytkownik został utworzony');
        setShowCreateModal(false);
        setValidationErrors({});
        queryClient.invalidateQueries('users');
      },
      onError: (error) => {
        const errorData = error.response?.data;
        
        // Parsuj błędy walidacji z express-validator
        if (errorData?.errors && Array.isArray(errorData.errors)) {
          const errors = {};
          errorData.errors.forEach(err => {
            const field = err.param || err.path;
            if (field) {
              errors[field] = err.msg || err.message;
            }
          });
          setValidationErrors(errors);
          // Pokaż również ogólny komunikat w toaście
          if (errorData.message) {
            toast.error(errorData.message);
          }
        } else if (errorData?.message) {
          // Spróbuj przypisać błąd do odpowiedniego pola, jeśli komunikat zawiera wskazówki
          const message = errorData.message.toLowerCase();
          const errors = {};
          
          if (message.includes('nazwą') || message.includes('username')) {
            errors.username = errorData.message;
          }
          if (message.includes('emailem') || message.includes('email')) {
            errors.email = errorData.message;
          }
          
          if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
          } else {
            // Jeśli nie można przypisać do konkretnego pola, pokaż tylko w toaście
            toast.error(errorData.message);
          }
        } else {
          toast.error('Błąd podczas tworzenia użytkownika');
        }
      }
    }
  );

  const updateUserMutation = useMutation(
    ({ userId, userData }) => userService.updateUser(userId, userData),
    {
      onSuccess: (response) => {
        if (!response || !response.user) {
          toast.error('Błąd: Nieprawidłowa odpowiedź z serwera');
          return;
        }

        // Zaktualizuj dane w cache z odpowiedzi serwera PRZED zamknięciem modala
        queryClient.setQueryData('users', (oldData) => {
          if (!oldData?.users) return oldData;
          return {
            ...oldData,
            users: oldData.users.map(u => 
              u._id === response.user._id || u.id === response.user._id || u._id === response.user.id
                ? { ...response.user, _id: response.user._id || response.user.id }
                : u
            )
          };
        });

        // Zamknij modal
        setShowEditModal(false);
        setSelectedUser(null);

        // Jeśli edytowany użytkownik to aktualnie zalogowany użytkownik, odśwież jego dane
        const editedUserId = response.user._id || response.user.id;
        const currentUserId = currentUser?._id || currentUser?.id;
        
        if (editedUserId && currentUserId && editedUserId.toString() === currentUserId.toString()) {
          refreshUser && refreshUser();
          toast.success('Użytkownik został zaktualizowany. Twoje menu zostało zaktualizowane.');
        } else {
          toast.success('Użytkownik został zaktualizowany');
          // Odśwież listę użytkowników po krótkim opóźnieniu, aby upewnić się że backend zapisał dane
          setTimeout(() => {
            queryClient.invalidateQueries('users');
          }, 500);
        }
      },
      onError: (error) => {
        console.error('Błąd aktualizacji użytkownika:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Błąd podczas aktualizacji użytkownika';
        toast.error(errorMessage);
      }
    }
  );

  const deleteUserMutation = useMutation(
    userService.deleteUser,
    {
      onSuccess: () => {
        toast.success('Użytkownik został usunięty');
        queryClient.invalidateQueries('users');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas usuwania użytkownika');
      }
    }
  );

  const assignFolderMutation = useMutation(
    ({ userId, deviceId, folderPath, folderName, permissions }) => 
      userService.assignFolderAccess(userId, deviceId, folderPath, folderName, permissions),
    {
      onSuccess: () => {
        toast.success('Dostęp do folderu został przypisany');
        setShowAssignModal(false);
        setSelectedUser(null);
        queryClient.invalidateQueries(['user-folder-access', selectedUser?._id]);
        queryClient.invalidateQueries('folder-access-summary');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas przypisywania dostępu');
      }
    }
  );

  const changePasswordMutation = useMutation(
    ({ userId, newPassword }) => userService.changeUserPassword(userId, newPassword),
    {
      onSuccess: () => {
        toast.success('Hasło użytkownika zostało zmienione');
        setShowChangePasswordModal(false);
        setSelectedUser(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas zmiany hasła');
      }
    }
  );

  const removeFolderAccessMutation = useMutation(
    ({ userId, accessId }) => userService.removeFolderAccess(userId, accessId),
    {
      onSuccess: () => {
        toast.success('Dostęp do folderu został usunięty');
        queryClient.invalidateQueries(['user-folder-access', selectedUser?._id]);
        queryClient.invalidateQueries('folder-access-summary');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas usuwania dostępu');
      }
    }
  );

  const bulkAssignFolderMutation = useMutation(
    ({ userIds, deviceId, folderPath, folderName, permissions }) =>
      userService.bulkAssignFolderAccess(userIds, deviceId, folderPath, folderName, permissions),
    {
      onSuccess: (data) => {
        toast.success(data.message || `Przypisano dostęp dla ${data.total} użytkowników`);
        setShowBulkFolderModal(false);
        setSelectedUserIds([]);
        queryClient.invalidateQueries('folder-access-summary');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd masowego przypisywania');
      }
    }
  );

  const bulkRemoveFolderMutation = useMutation(
    ({ userIds, deviceId, folderPath }) =>
      userService.bulkRemoveFolderAccess(userIds, deviceId, folderPath),
    {
      onSuccess: (data) => {
        toast.success(data.message || `Usunięto dostęp u ${data.removed} użytkowników`);
        setShowBulkFolderModal(false);
        setSelectedUserIds([]);
        queryClient.invalidateQueries('folder-access-summary');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd masowego usuwania');
      }
    }
  );

  const handleCreateUser = (e) => {
    e.preventDefault();
    // Wyczyść błędy przed wysłaniem
    setValidationErrors({});
    const formData = new FormData(e.target);
    const userData = {
      username: formData.get('username'),
      email: formData.get('email'),
      password: formData.get('password'),
      role: formData.get('role'),
      moduleAccess: {
        upload: formData.get('module_upload') === 'on',
        planner: formData.get('module_planner') === 'on',
        kanban: formData.get('module_kanban') === 'on',
        mapa: formData.get('module_mapa') === 'on',
        ekipaSerwisowa: formData.get('module_ekipaSerwisowa') === 'on',
        farmyFoto: formData.get('module_farmyFoto') === 'on',
        history: formData.get('module_history') === 'on',
        urlopy: formData.get('module_urlopy') === 'on',
        delegacje: formData.get('module_delegacje') === 'on',
        kadry: formData.get('module_urlopy') === 'on' || formData.get('module_delegacje') === 'on',
        cctv: formData.get('module_cctv') === 'on',
        dokumenty: formData.get('module_dokumenty') === 'on',
        announcements: formData.get('module_announcements') === 'on'
      },
      firstName: formData.get('firstName') || undefined,
      lastName: formData.get('lastName') || undefined,
      phone: formData.get('phone') || undefined,
      position: formData.get('position') || undefined,
      hireDate: formData.get('hireDate') || undefined,
      department: formData.get('department') || undefined,
      przełożony: formData.get('przełożony') || undefined,
      notes: formData.get('notes') || undefined
    };
    createUserMutation.mutate(userData);
  };

  const handleFieldChange = (fieldName) => {
    // Wyczyść błąd dla danego pola, gdy użytkownik zacznie pisać
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleAssignFolder = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const selectedFolderData = formData.get('selectedFolder');
    
    if (!selectedFolderData) {
      toast.error('Wybierz folder');
      return;
    }
    
    const folderData = JSON.parse(selectedFolderData);
    const permissions = {
      read: formData.get('read') === 'on',
      write: formData.get('write') === 'on',
      upload: formData.get('upload') === 'on'
    };
    
    assignFolderMutation.mutate({
      userId: selectedUser._id,
      deviceId: folderData.deviceId,
      folderPath: folderData.folderPath,
      folderName: folderData.folderName,
      permissions
    });
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm('Czy na pewno chcesz usunąć tego użytkownika?')) {
      deleteUserMutation.mutate(userId);
    }
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newPassword = formData.get('newPassword');
    const confirmPassword = formData.get('confirmPassword');

    if (newPassword !== confirmPassword) {
      toast.error('Hasła nie są identyczne');
      return;
    }

    changePasswordMutation.mutate({
      userId: selectedUser._id,
      newPassword
    });
  };

  const handleRemoveFolderAccess = (accessId) => {
    if (window.confirm('Czy na pewno chcesz usunąć dostęp do tego folderu?')) {
      removeFolderAccessMutation.mutate({
        userId: selectedUser._id,
        accessId
      });
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleBulkAssignFolder = (e) => {
    e.preventDefault();
    if (selectedUserIds.length === 0) {
      toast.error('Zaznacz przynajmniej jednego użytkownika');
      return;
    }
    const formData = new FormData(e.target);
    const selectedFolderData = formData.get('selectedFolder');
    if (!selectedFolderData) {
      toast.error('Wybierz folder');
      return;
    }
    const folderData = JSON.parse(selectedFolderData);
    const permissions = {
      read: formData.get('read') === 'on',
      write: formData.get('write') === 'on',
      upload: formData.get('upload') === 'on'
    };
    bulkAssignFolderMutation.mutate({
      userIds: selectedUserIds,
      deviceId: folderData.deviceId,
      folderPath: folderData.folderPath,
      folderName: folderData.folderName,
      permissions
    });
  };

  const handleBulkRemoveFolder = (e) => {
    e.preventDefault();
    if (selectedUserIds.length === 0) {
      toast.error('Zaznacz przynajmniej jednego użytkownika');
      return;
    }
    const form = document.getElementById('bulk-folder-form');
    if (!form) return;
    const formData = new FormData(form);
    const selectedFolderData = formData.get('selectedFolder');
    if (!selectedFolderData) {
      toast.error('Wybierz folder');
      return;
    }
    const folderData = JSON.parse(selectedFolderData);
    if (!window.confirm(
      `Usunąć dostęp do „${folderData.folderName}” u ${selectedUserIds.length} użytkowników?`
    )) {
      return;
    }
    bulkRemoveFolderMutation.mutate({
      userIds: selectedUserIds,
      deviceId: folderData.deviceId,
      folderPath: folderData.folderPath
    });
  };

  const handleEditUser = (e) => {
    e.preventDefault();
    
    if (!selectedUser || !selectedUser._id) {
      toast.error('Błąd: Brak wybranego użytkownika');
      return;
    }

    const formData = new FormData(e.target);
    const moduleAccess = {
      upload: formData.get('module_upload') === 'on',
      planner: formData.get('module_planner') === 'on',
      kanban: formData.get('module_kanban') === 'on',
      mapa: formData.get('module_mapa') === 'on',
      ekipaSerwisowa: formData.get('module_ekipaSerwisowa') === 'on',
      farmyFoto: formData.get('module_farmyFoto') === 'on',
      history: formData.get('module_history') === 'on',
      urlopy: formData.get('module_urlopy') === 'on',
      delegacje: formData.get('module_delegacje') === 'on',
      kadry: formData.get('module_urlopy') === 'on' || formData.get('module_delegacje') === 'on',
      cctv: formData.get('module_cctv') === 'on',
      dokumenty: formData.get('module_dokumenty') === 'on',
      announcements: formData.get('module_announcements') === 'on'
    };
    
    console.log('Frontend - Sending update with moduleAccess:', moduleAccess);
    
    // Pobierz dane pracownika - zawsze wysyłaj, nawet jeśli puste (aby móc je usunąć)
    const firstName = formData.get('firstName')?.trim() || '';
    const lastName = formData.get('lastName')?.trim() || '';
    const phone = formData.get('phone')?.trim() || '';
    const position = formData.get('position')?.trim() || '';
    const hireDate = formData.get('hireDate') || '';
    const department = formData.get('department') || '';
    const przełożony = formData.get('przełożony') || '';
    const dniUrlopuRocznie = formData.get('dniUrlopuRocznie');
    const notes = formData.get('notes')?.trim() || '';
    
    const userData = {
      username: formData.get('username')?.trim(),
      email: formData.get('email')?.trim(),
      role: formData.get('role'),
      moduleAccess,
      firstName: firstName,
      lastName: lastName,
      phone: phone,
      position: position,
      hireDate: hireDate,
      department: department || undefined,
      przełożony: przełożony || undefined,
      dniUrlopuRocznie: dniUrlopuRocznie ? parseInt(dniUrlopuRocznie, 10) : undefined,
      notes: notes
    };

    console.log('Frontend - Sending user update data:', userData);

    // Walidacja
    if (!userData.username || !userData.email) {
      toast.error('Nazwa użytkownika i email są wymagane');
      return;
    }
    
    updateUserMutation.mutate({
      userId: selectedUser._id,
      userData
    });
  };

  const getRoleBadge = (role) => {
    if (role === 'super admin') {
      return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-ink text-white">
        Super Admin
      </span>;
    }
    if (role === 'admin') {
      return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-ink text-white">
        Administrator
      </span>;
    }
    return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
      Użytkownik
    </span>;
  };

  const AVATAR_COLORS = [
    'bg-slate-800', 'bg-emerald-700', 'bg-indigo-700', 'bg-rose-700',
    'bg-amber-700', 'bg-cyan-700', 'bg-violet-700', 'bg-orange-700'
  ];

  const getAvatarColor = (id) => {
    const str = String(id || '');
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  };

  const getInitials = (user) => {
    const first = user.firstName?.[0] || user.username?.[0] || '?';
    const second = user.lastName?.[0] || user.username?.[1] || '';
    return (first + second).toUpperCase();
  };

  const getDisplayName = (user) =>
    user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username;

  const formatLastActivity = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const now = new Date();
    const time = d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    if (d.toDateString() === now.toDateString()) return `Dzisiaj, ${time}`;
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return `Wczoraj, ${time}`;
    return `${d.toLocaleDateString('pl-PL')}, ${time}`;
  };

  const MODULE_BADGES = [
    { key: 'upload', label: 'UP', color: 'bg-green-100 text-green-700' },
    { key: 'planner', label: 'PLN', color: 'bg-purple-100 text-purple-700' },
    { key: 'kanban', label: 'KAN', color: 'bg-blue-100 text-blue-700' },
    { key: 'mapa', label: 'MAPA', color: 'bg-orange-100 text-orange-700' },
    { key: 'ekipaSerwisowa', label: 'SERWIS', color: 'bg-yellow-100 text-yellow-700' },
    { key: 'farmyFoto', label: 'FOTO', color: 'bg-pink-100 text-pink-700' },
    { key: 'history', label: 'HIST', color: 'bg-gray-100 text-gray-700' },
    { key: 'urlopy', label: 'URL', color: 'bg-cyan-100 text-cyan-700' },
    { key: 'delegacje', label: 'DEL', color: 'bg-cyan-100 text-cyan-700' },
    { key: 'cctv', label: 'CCTV', color: 'bg-indigo-100 text-indigo-700' },
    { key: 'dokumenty', label: 'DOK', color: 'bg-teal-100 text-teal-700' },
    { key: 'announcements', label: 'OGL', color: 'bg-rose-100 text-rose-700' }
  ];

  const getUserModuleBadges = (user) => MODULE_BADGES.filter((m) => user.moduleAccess?.[m.key]);

  const handleEditClick = (user) => {
    queryClient.invalidateQueries('users').then(() => {
      const freshUser = users?.users?.find((u) => u._id === user._id);
      setSelectedUser(freshUser || user);
      setShowEditModal(true);
    });
  };

  const handleImpersonate = async (user) => {
    if (window.confirm(`Czy na pewno chcesz zalogować się jako ${getDisplayName(user)}?`)) {
      const result = await impersonate(user._id);
      if (result.success) {
        window.location.href = '/dashboard';
      }
    }
  };

  if (usersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const allUsers = users?.users || [];
  const totalUsers = allUsers.length;
  const adminCount = allUsers.filter(u => u.role === 'admin' || u.role === 'super admin').length;
  const inactiveCount = allUsers.filter(u => u.isActive === false).length;
  const activeTodayCount = allUsers.filter(u => {
    if (!u.lastLogin) return false;
    return new Date(u.lastLogin).toDateString() === new Date().toDateString();
  }).length;

  const tabs = [
    { key: 'all', label: 'Wszyscy', count: totalUsers },
    { key: 'active', label: 'Aktywni', count: totalUsers - inactiveCount },
    { key: 'inactive', label: 'Nieaktywni', count: inactiveCount }
  ];

  let filteredUsers = allUsers;
  if (activeTab === 'active') filteredUsers = filteredUsers.filter(u => u.isActive !== false);
  if (activeTab === 'inactive') filteredUsers = filteredUsers.filter(u => u.isActive === false);
  if (searchQuery.trim()) {
    const q = searchQuery.trim().toLowerCase();
    filteredUsers = filteredUsers.filter(u =>
      u.username?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.department?.toLowerCase().includes(q) ||
      getDisplayName(u).toLowerCase().includes(q)
    );
  }

  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const page = Math.min(currentPage, totalPages);
  const paginatedUsers = filteredUsers.slice((page - 1) * pageSize, page * pageSize);

  const pageUserIds = paginatedUsers.map((u) => u._id);
  const allPageSelected =
    pageUserIds.length > 0 && pageUserIds.every((id) => selectedUserIds.includes(id));
  const allFilteredSelected =
    filteredUsers.length > 0 &&
    filteredUsers.every((u) => selectedUserIds.includes(u._id));

  const toggleSelectAllPage = () => {
    if (allPageSelected) {
      setSelectedUserIds((prev) => prev.filter((id) => !pageUserIds.includes(id)));
    } else {
      setSelectedUserIds((prev) => [...new Set([...prev, ...pageUserIds])]);
    }
  };

  const selectAllFiltered = () => {
    setSelectedUserIds(filteredUsers.map((u) => u._id));
  };

  const clearSelection = () => setSelectedUserIds([]);

  const statCards = [
    { label: 'Użytkowników', value: totalUsers, hint: 'łącznie w systemie', icon: Users, color: 'text-primary-600', bg: 'bg-primary-50' },
    { label: 'Administratorów', value: adminCount, hint: `${totalUsers ? Math.round((adminCount / totalUsers) * 100) : 0}% wszystkich`, icon: Shield, color: 'text-violet-600', bg: 'bg-violet-50' },
    { label: 'Aktywnych dzisiaj', value: activeTodayCount, hint: `${totalUsers ? Math.round((activeTodayCount / totalUsers) * 100) : 0}% wszystkich`, icon: UserCheck, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Nieaktywnych', value: inactiveCount, hint: 'zablokowani / wyłączeni', icon: UserX, color: 'text-red-600', bg: 'bg-red-50' }
  ];

  const renderRowActions = (user) => (
    <div className="flex items-center justify-end gap-1">
      {currentUser?.role === 'super admin' && user._id !== currentUser._id && (
        <button
          onClick={() => handleImpersonate(user)}
          className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
          title="Zaloguj się jako ten użytkownik"
        >
          <LogIn className="h-4 w-4" />
        </button>
      )}
      <button
        onClick={() => handleEditClick(user)}
        className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
        title="Edytuj użytkownika"
      >
        <Edit className="h-4 w-4" />
      </button>
      <button
        ref={openMenuUserId === user._id ? actionsMenuTriggerRef : null}
        onClick={(e) => openActionsMenu(e, user._id)}
        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        title="Więcej akcji"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {openMenuUserId === user._id && menuAnchor && createPortal(
        <div
          ref={actionsMenuRef}
          style={{ position: 'fixed', top: menuAnchor.top, left: menuAnchor.left }}
          className="w-56 bg-white rounded-xl shadow-lg border border-gray-100 z-50 py-1.5 text-sm"
        >
          <button
            onClick={() => {
              setSelectedUser(user);
              setShowAssignModal(true);
              setOpenMenuUserId(null);
              setMenuAnchor(null);
            }}
            className="w-full flex items-center gap-2.5 px-3.5 py-2 text-gray-700 hover:bg-gray-50"
          >
            <FolderOpen className="h-4 w-4 text-blue-500" />
            Dostęp do folderu
          </button>
          <button
            onClick={() => {
              setSelectedUser(user);
              setShowChangePasswordModal(true);
              setOpenMenuUserId(null);
              setMenuAnchor(null);
            }}
            className="w-full flex items-center gap-2.5 px-3.5 py-2 text-gray-700 hover:bg-gray-50"
          >
            <Key className="h-4 w-4 text-emerald-500" />
            Zmień hasło
          </button>
          <div className="my-1 border-t border-gray-100" />
          <button
            onClick={() => {
              setOpenMenuUserId(null);
              setMenuAnchor(null);
              handleDeleteUser(user._id);
            }}
            disabled={deleteUserMutation.isLoading}
            className="w-full flex items-center gap-2.5 px-3.5 py-2 text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
            Usuń użytkownika
          </button>
        </div>,
        document.body
      )}
    </div>
  );

  return (
    <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="page-title">Zarządzanie użytkownikami</h1>
          <p className="page-subtitle">Dodawaj użytkowników i przydzielaj im dostęp do zasobów.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setShowBulkFolderModal(true)}
            disabled={selectedUserIds.length === 0}
            className="btn-secondary flex items-center disabled:opacity-50"
            title={selectedUserIds.length === 0 ? 'Zaznacz użytkowników na liście' : 'Masowa zmiana folderów'}
          >
            <FolderOpen className="h-4 w-4 mr-2" />
            Masowa zmiana folderów
            {selectedUserIds.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-primary-600 text-white text-[10px] font-bold">
                {selectedUserIds.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Dodaj użytkownika
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="card !rounded-2xl px-5 py-4 border-black/[0.03]">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-2xl font-bold text-ink tabular-nums">{stat.value}</p>
                  <p className="text-sm font-medium text-ink mt-0.5">{stat.label}</p>
                  <p className="text-xs text-ink-faint mt-1 truncate">{stat.hint}</p>
                </div>
                <div className={`p-2.5 rounded-xl ${stat.bg} flex-shrink-0`}>
                  <Icon className={`h-5 w-5 ${stat.color}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Users List */}
      <div className="card">
        <div className="card-header flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-1 bg-surface rounded-xl p-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                  activeTab === tab.key
                    ? 'bg-white text-ink shadow-sm'
                    : 'text-ink-soft hover:text-ink'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Szukaj użytkownika lub działu..."
                className="input !pl-9 !py-2 w-56"
              />
            </div>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary-100 text-primary-600'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="Widok kafelkowy"
            >
              <Grid3x3 className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary-100 text-primary-600'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="Widok listy"
            >
              <List className="h-5 w-5" />
            </button>
          </div>
        </div>

        {selectedUserIds.length > 0 && (
          <div className="mx-4 mt-4 px-4 py-3 rounded-xl bg-primary-50 border border-primary-100 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-ink">
              Zaznaczono <span className="font-bold">{selectedUserIds.length}</span>
              {allFilteredSelected ? ' (wszyscy z filtra)' : ` z ${filteredUsers.length} na filtrze`}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {!allFilteredSelected && (
                <button
                  type="button"
                  onClick={selectAllFiltered}
                  className="text-xs font-semibold text-primary-700 hover:underline"
                >
                  Zaznacz wszystkich z filtra ({filteredUsers.length})
                </button>
              )}
              <button type="button" onClick={clearSelection} className="text-xs font-semibold text-ink-soft hover:underline">
                Odznacz
              </button>
              <button
                type="button"
                onClick={() => setShowBulkFolderModal(true)}
                className="btn-primary !py-1.5 !px-3 text-xs"
              >
                Zmień foldery
              </button>
            </div>
          </div>
        )}

        <div className={viewMode === 'list' ? '' : 'card-body'}>
          {filteredUsers.length === 0 ? (
            <div className="text-center py-14">
              <Users className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-ink">Brak użytkowników spełniających kryteria</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {paginatedUsers.map((user) => {
                const badges = getUserModuleBadges(user);
                const userFolders = folderAccessSummary[user._id] || [];
                const isChecked = selectedUserIds.includes(user._id);
                return (
                  <div key={user._id} className={`flex items-center justify-between p-4 bg-white rounded-xl border gap-3 ${isChecked ? 'border-primary-300 bg-primary-50/40' : 'border-gray-100'}`}>
                    <div className="flex items-center flex-1 min-w-0">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleUserSelection(user._id)}
                        className="mr-3 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 flex-shrink-0"
                      />
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${getAvatarColor(user._id)}`}>
                        {getInitials(user)}
                      </div>
                      <div className="ml-3 flex-1 min-w-0">
                        <p className="text-sm font-semibold text-ink truncate">{getDisplayName(user)}</p>
                        <p className="text-xs text-ink-faint truncate">{user.email}</p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1">
                          {getRoleBadge(user.role)}
                          {badges.slice(0, 3).map((b) => (
                            <span key={b.key} className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${b.color}`}>
                              {b.label}
                            </span>
                          ))}
                          {badges.length > 3 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600">
                              +{badges.length - 3}
                            </span>
                          )}
                          {userFolders.length > 0 && (
                            <span
                              title={userFolders.map((f) => `${f.folderName} (${f.deviceName})`).join('\n')}
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-50 text-sky-700"
                            >
                              <FolderOpen className="h-3 w-3" />
                              {userFolders.length}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {renderRowActions(user)}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-[11px] font-bold uppercase tracking-wide text-ink-faint">
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={toggleSelectAllPage}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        title="Zaznacz wszystkich na stronie"
                      />
                    </th>
                    <th className="px-3 py-3 font-bold">Użytkownik</th>
                    <th className="px-3 py-3 font-bold">Rola</th>
                    <th className="px-3 py-3 font-bold">Dział</th>
                    <th className="px-3 py-3 font-bold">Dostęp</th>
                    <th className="px-3 py-3 font-bold">Foldery</th>
                    <th className="px-3 py-3 font-bold">Ostatnia aktywność</th>
                    <th className="px-3 py-3 font-bold">Status</th>
                    <th className="px-6 py-3 font-bold text-right">Akcje</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {paginatedUsers.map((user) => {
                    const badges = getUserModuleBadges(user);
                    const userFolders = folderAccessSummary[user._id] || [];
                    const isChecked = selectedUserIds.includes(user._id);
                    return (
                      <tr key={user._id} className={`hover:bg-surface/60 transition-colors ${isChecked ? 'bg-primary-50/50' : ''}`}>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleUserSelection(user._id)}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`h-9 w-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${getAvatarColor(user._id)}`}>
                              {getInitials(user)}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-ink truncate">{getDisplayName(user)}</p>
                              <p className="text-xs text-ink-faint truncate">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3">{getRoleBadge(user.role)}</td>
                        <td className="px-3 py-3 text-ink-soft whitespace-nowrap">{user.department || '—'}</td>
                        <td className="px-3 py-3">
                          <div className="flex flex-wrap items-center gap-1 max-w-[220px]">
                            {badges.slice(0, 3).map((b) => (
                              <span key={b.key} className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${b.color}`}>
                                {b.label}
                              </span>
                            ))}
                            {badges.length > 3 && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600">
                                +{badges.length - 3}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          {userFolders.length === 0 ? (
                            <span className="text-ink-faint">—</span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedUser(user);
                                setShowAssignModal(true);
                              }}
                              title={userFolders.map((f) => `${f.folderName} (${f.deviceName})`).join('\n')}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-sky-50 text-sky-700 hover:bg-sky-100 transition-colors"
                            >
                              <FolderOpen className="h-3.5 w-3.5" />
                              {userFolders.length}
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-3 text-ink-soft whitespace-nowrap">{formatLastActivity(user.lastLogin)}</td>
                        <td className="px-3 py-3">
                          <span className={user.isActive === false ? 'status-disconnected' : 'status-online'}>
                            {user.isActive === false ? 'Nieaktywny' : 'Aktywny'}
                          </span>
                        </td>
                        <td className="px-6 py-3">{renderRowActions(user)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {filteredUsers.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-ink-faint">
              {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, filteredUsers.length)} z {filteredUsers.length} użytkownikami
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  p === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 text-xs text-ink-faint">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`h-7 w-7 rounded-lg text-xs font-semibold transition-colors ${
                        p === page
                          ? 'bg-primary-600 text-white'
                          : 'text-ink-soft hover:bg-gray-100'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Dodaj nowego użytkownika
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setValidationErrors({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nazwa użytkownika
                </label>
                <input
                  type="text"
                  name="username"
                  required
                  onChange={() => handleFieldChange('username')}
                  className={`input ${validationErrors.username ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Wprowadź nazwę użytkownika"
                />
                {validationErrors.username && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.username}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  onChange={() => handleFieldChange('email')}
                  className={`input ${validationErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Wprowadź email"
                />
                {validationErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hasło
                </label>
                <input
                  type="password"
                  name="password"
                  required
                  onChange={() => handleFieldChange('password')}
                  className={`input ${validationErrors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Wprowadź hasło"
                />
                {validationErrors.password && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rola
                </label>
                <select name="role" className="input">
                  <option value="user">Użytkownik</option>
                  {currentUser?.role === 'super admin' && (
                    <option value="admin">Administrator</option>
                  )}
                  {currentUser?.role === 'super admin' && (
                    <option value="super admin">Super Administrator</option>
                  )}
                </select>
              </div>

              {/* Dane pracownika */}
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Dane pracownika (opcjonalne)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Imię
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      className="input"
                      placeholder="Jan"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nazwisko
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      className="input"
                      placeholder="Kowalski"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      className="input"
                      placeholder="+48 123 456 789"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stanowisko
                    </label>
                    <select name="position" className="input">
                      <option value="">— Wybierz stanowisko —</option>
                      {POSITIONS.map(pos => (
                        <option key={pos} value={pos}>{pos}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data zatrudnienia
                    </label>
                    <input
                      type="date"
                      name="hireDate"
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dział
                    </label>
                    <select name="department" className="input">
                      <option value="">— Wybierz dział —</option>
                      {DEPARTMENTS.map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Przełożony główny
                    </label>
                    <select name="przełożony" className="input">
                      <option value="">— Brak —</option>
                      {users?.users?.filter(u => u.isActive !== false).map(u => (
                        <option key={u._id} value={u._id}>
                          {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.username}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notatki
                  </label>
                  <textarea
                    name="notes"
                    rows={3}
                    className="input"
                    placeholder="Dodatkowe informacje o pracowniku..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dostęp do modułów
                </label>
                <div className="grid grid-cols-2 gap-3 border border-gray-200 rounded-lg p-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_upload"
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Upload zdjęć</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_planner"
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Planer</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_kanban"
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Kanban</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_mapa"
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Mapa</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_ekipaSerwisowa"
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Ekipa Serwisowa</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_farmyFoto"
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Farmy Foto</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_history"
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Historia</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_urlopy"
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Kadry – Urlopy</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_delegacje"
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Kadry – Delegacje</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_cctv"
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">CCTV</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_dokumenty"
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Dokumenty</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_announcements"
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Ogłoszenia</span>
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Zaznacz moduły, do których użytkownik ma mieć dostęp w dashboardzie
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setValidationErrors({});
                  }}
                  className="flex-1 btn-secondary"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={createUserMutation.isLoading}
                  className="flex-1 btn-primary"
                >
                  {createUserMutation.isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    'Utwórz użytkownika'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Folder Access Modal */}
      {showAssignModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Zarządzanie dostępem do folderów - {selectedUser.username}
              </h3>
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedUser(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            {/* Existing Folder Access */}
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-900 mb-3">
                Obecne dostępy do folderów
              </h4>
              {userAccessLoading ? (
                <div className="flex items-center justify-center py-4">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Ładowanie dostępu...</span>
                </div>
              ) : userFolderAccess?.userAccess?.length > 0 ? (
                <div className="space-y-2">
                  {userFolderAccess.userAccess.map((access) => (
                    <div key={access._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{access.folderName}</div>
                        <div className="text-sm text-gray-500">
                          {access.device?.name} - {access.folderPath}
                        </div>
                        <div className="flex space-x-2 mt-1">
                          {access.permissions.read && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Odczyt
                            </span>
                          )}
                          {access.permissions.write && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Zapis
                            </span>
                          )}
                          {access.permissions.upload && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Upload
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveFolderAccess(access._id)}
                        disabled={removeFolderAccessMutation.isLoading}
                        className="text-red-600 hover:text-red-800 ml-2"
                        title="Usuń dostęp"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  Użytkownik nie ma jeszcze dostępu do żadnych folderów
                </div>
              )}
            </div>

            {/* Add New Folder Access */}
            <div className="border-t pt-4">
              <h4 className="text-md font-medium text-gray-900 mb-3">
                Dodaj nowy dostęp do folderu
              </h4>
              <form onSubmit={handleAssignFolder} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Użytkownik
                </label>
                <input
                  type="text"
                  value={selectedUser.username}
                  readOnly
                  className="input bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Wybierz folder
                </label>
                {foldersLoading ? (
                  <div className="input flex items-center justify-center">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Ładowanie folderów...</span>
                  </div>
                ) : (
                  <select name="selectedFolder" required className="input">
                    <option value="">Wybierz folder</option>
                    {availableFolders?.availableFolders?.map((folder) => (
                      <option key={`${folder.deviceId}-${folder.path}`} value={JSON.stringify({
                        deviceId: folder.deviceId,
                        folderPath: folder.path,
                        folderName: folder.name,
                        deviceName: folder.deviceName
                      })}>
                        {folder.name} - {folder.deviceName} ({folder.path})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Uprawnienia
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="read"
                      defaultChecked
                      className="mr-2"
                    />
                    <span className="text-sm">Odczyt</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="write"
                      className="mr-2"
                    />
                    <span className="text-sm">Zapis</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="upload"
                      defaultChecked
                      className="mr-2"
                    />
                    <span className="text-sm">Upload zdjęć</span>
                  </label>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1 btn-secondary"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={assignFolderMutation.isLoading}
                  className="flex-1 btn-primary"
                >
                  {assignFolderMutation.isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    'Przypisz dostęp'
                  )}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Folder Access Modal */}
      {showBulkFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-card-hover">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-ink">
                  Masowa zmiana dostępu do folderów
                </h3>
                <p className="text-sm text-ink-soft mt-0.5">
                  Zaznaczono {selectedUserIds.length}{' '}
                  {selectedUserIds.length === 1 ? 'użytkownika' : 'użytkowników'}
                </p>
              </div>
              <button
                onClick={() => setShowBulkFolderModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {selectedUserIds.length === 0 ? (
              <div className="text-center py-8 text-ink-soft text-sm">
                Najpierw zaznacz użytkowników na liście (checkboxy), albo użyj
                „Zaznacz wszystkich z filtra”.
              </div>
            ) : (
              <>
                <div className="mb-4 p-3 rounded-xl bg-surface border border-black/[0.04] max-h-28 overflow-y-auto">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-ink-faint mb-2">
                    Wybrani użytkownicy
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {(users?.users || [])
                      .filter((u) => selectedUserIds.includes(u._id))
                      .slice(0, 40)
                      .map((u) => (
                        <span
                          key={u._id}
                          className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium bg-white border border-gray-200 text-ink"
                        >
                          {u.firstName && u.lastName
                            ? `${u.firstName} ${u.lastName}`
                            : u.username}
                        </span>
                      ))}
                    {selectedUserIds.length > 40 && (
                      <span className="text-xs text-ink-faint">
                        +{selectedUserIds.length - 40} więcej
                      </span>
                    )}
                  </div>
                </div>

                <form id="bulk-folder-form" onSubmit={handleBulkAssignFolder} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Wybierz folder
                    </label>
                    {foldersLoading ? (
                      <div className="input flex items-center justify-center">
                        <LoadingSpinner size="sm" />
                        <span className="ml-2">Ładowanie folderów...</span>
                      </div>
                    ) : (
                      <select name="selectedFolder" required className="input">
                        <option value="">Wybierz folder</option>
                        {availableFolders?.availableFolders?.map((folder) => (
                          <option
                            key={`${folder.deviceId}-${folder.path}`}
                            value={JSON.stringify({
                              deviceId: folder.deviceId,
                              folderPath: folder.path,
                              folderName: folder.name,
                              deviceName: folder.deviceName
                            })}
                          >
                            {folder.name} - {folder.deviceName} ({folder.path})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Uprawnienia (przy przypisaniu)
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input type="checkbox" name="read" defaultChecked className="mr-2" />
                        <span className="text-sm">Odczyt</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" name="write" className="mr-2" />
                        <span className="text-sm">Zapis</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" name="upload" defaultChecked className="mr-2" />
                        <span className="text-sm">Upload zdjęć</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowBulkFolderModal(false)}
                      className="flex-1 btn-secondary"
                    >
                      Anuluj
                    </button>
                    <button
                      type="button"
                      onClick={handleBulkRemoveFolder}
                      disabled={bulkRemoveFolderMutation.isLoading || bulkAssignFolderMutation.isLoading}
                      className="flex-1 btn border border-red-200 text-red-700 bg-red-50 hover:bg-red-100"
                      form="bulk-folder-form"
                    >
                      {bulkRemoveFolderMutation.isLoading ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        'Usuń ten folder u zaznaczonych'
                      )}
                    </button>
                    <button
                      type="submit"
                      disabled={bulkAssignFolderMutation.isLoading || bulkRemoveFolderMutation.isLoading}
                      className="flex-1 btn-primary"
                    >
                      {bulkAssignFolderMutation.isLoading ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        `Przypisz wszystkim (${selectedUserIds.length})`
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div key={`edit-modal-${selectedUser._id}`} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Edytuj użytkownika
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedUser(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleEditUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nazwa użytkownika
                </label>
                <input
                  type="text"
                  name="username"
                  required
                  defaultValue={selectedUser.username}
                  className="input"
                  placeholder="Wprowadź nazwę użytkownika"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  defaultValue={selectedUser.email}
                  className="input"
                  placeholder="Wprowadź email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rola
                </label>
                <select 
                  name="role" 
                  className="input" 
                  defaultValue={selectedUser.role}
                  disabled={selectedUser.role === 'super admin' && currentUser?.role !== 'super admin'}
                >
                  <option value="user">Użytkownik</option>
                  {(currentUser?.role === 'super admin' || currentUser?.role === 'admin') && (
                    <option value="admin">Administrator</option>
                  )}
                  {currentUser?.role === 'super admin' && (
                    <option value="super admin">Super Administrator</option>
                  )}
                </select>
                {selectedUser.role === 'super admin' && currentUser?.role !== 'super admin' && (
                  <p className="mt-1 text-xs text-gray-500">
                    Nie możesz zmieniać roli super administratora
                  </p>
                )}
              </div>

              {/* Dane pracownika */}
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Dane pracownika (opcjonalne)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Imię
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      defaultValue={selectedUser.firstName || ''}
                      className="input"
                      placeholder="Jan"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nazwisko
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      defaultValue={selectedUser.lastName || ''}
                      className="input"
                      placeholder="Kowalski"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      defaultValue={selectedUser.phone || ''}
                      className="input"
                      placeholder="+48 123 456 789"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stanowisko
                    </label>
                    <select name="position" className="input" defaultValue={selectedUser.position || ''}>
                      <option value="">— Wybierz stanowisko —</option>
                      {POSITIONS.map(pos => (
                        <option key={pos} value={pos}>{pos}</option>
                      ))}
                      {selectedUser.position && !POSITIONS.includes(selectedUser.position) && (
                        <option value={selectedUser.position}>{selectedUser.position} (obecne)</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data zatrudnienia
                    </label>
                    <input
                      type="date"
                      name="hireDate"
                      defaultValue={selectedUser.hireDate ? new Date(selectedUser.hireDate).toISOString().split('T')[0] : ''}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dział
                    </label>
                    <select name="department" className="input" defaultValue={selectedUser.department || ''}>
                      <option value="">— Wybierz dział —</option>
                      {DEPARTMENTS.map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                      {selectedUser.department && !DEPARTMENTS.includes(selectedUser.department) && (
                        <option value={selectedUser.department}>{selectedUser.department} (obecne — wybierz nowy)</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Przełożony główny
                    </label>
                    <select name="przełożony" className="input" defaultValue={selectedUser.przełożony?._id || selectedUser.przełożony || ''}>
                      <option value="">— Brak —</option>
                      {users?.users?.filter(u => u._id !== selectedUser._id && u.isActive !== false).map(u => (
                        <option key={u._id} value={u._id}>
                          {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.username}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notatki
                  </label>
                  <textarea
                    name="notes"
                    rows={3}
                    defaultValue={selectedUser.notes || ''}
                    className="input"
                    placeholder="Dodatkowe informacje o pracowniku..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dostęp do modułów
                </label>
                <div className="grid grid-cols-2 gap-3 border border-gray-200 rounded-lg p-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_upload"
                      defaultChecked={selectedUser.moduleAccess?.upload || false}
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Upload zdjęć</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_planner"
                      defaultChecked={selectedUser.moduleAccess?.planner || false}
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Planer</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_kanban"
                      defaultChecked={selectedUser.moduleAccess?.kanban || false}
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Kanban</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_mapa"
                      defaultChecked={selectedUser.moduleAccess?.mapa || false}
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Mapa</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_ekipaSerwisowa"
                      defaultChecked={selectedUser.moduleAccess?.ekipaSerwisowa || false}
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Ekipa Serwisowa</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_farmyFoto"
                      defaultChecked={selectedUser.moduleAccess?.farmyFoto || false}
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Farmy Foto</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_history"
                      defaultChecked={selectedUser.moduleAccess?.history || false}
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Historia</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_urlopy"
                      defaultChecked={selectedUser.moduleAccess?.urlopy ?? selectedUser.moduleAccess?.kadry ?? false}
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Kadry – Urlopy</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_delegacje"
                      defaultChecked={selectedUser.moduleAccess?.delegacje ?? selectedUser.moduleAccess?.kadry ?? false}
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Kadry – Delegacje</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_cctv"
                      defaultChecked={selectedUser.moduleAccess?.cctv || false}
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">CCTV</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_dokumenty"
                      defaultChecked={selectedUser.moduleAccess?.dokumenty || false}
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Dokumenty</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="module_announcements"
                      defaultChecked={selectedUser.moduleAccess?.announcements || false}
                      className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Ogłoszenia</span>
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Zaznacz moduły, do których użytkownik ma mieć dostęp w dashboardzie
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1 btn-secondary"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={updateUserMutation.isLoading}
                  className="flex-1 btn-primary"
                >
                  {updateUserMutation.isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    'Zapisz zmiany'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Zmień hasło użytkownika
              </h3>
              <button
                onClick={() => {
                  setShowChangePasswordModal(false);
                  setSelectedUser(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Użytkownik
                </label>
                <input
                  type="text"
                  value={selectedUser.username}
                  readOnly
                  className="input bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nowe hasło
                </label>
                <input
                  type="password"
                  name="newPassword"
                  required
                  minLength={6}
                  className="input"
                  placeholder="Wprowadź nowe hasło"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Potwierdź hasło
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  required
                  minLength={6}
                  className="input"
                  placeholder="Potwierdź nowe hasło"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePasswordModal(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1 btn-secondary"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={changePasswordMutation.isLoading}
                  className="flex-1 btn-primary"
                >
                  {changePasswordMutation.isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    'Zmień hasło'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
